<template>
  <div class="theme-editor">
    <router-link  class="btn btnAccent mb-3" to="/Venue-List">Back to Venues</router-link>
    <!-- Tabs -->
    <div class="tab-row">
      <button
        v-for="screen in screens"
        :key="screen"
        @click="activeScreen = screen"
        :class="['tab-btn', { active: screen === activeScreen }]"
      >
        {{ screen }}
      </button>
    </div>

    <!-- Customize Color Panel -->
    <div class="customize-card">
      <div class="customize-header">
        <h2>Customize Colors
          <button class="tooltip-btn" type="button" v-on:click="imageInfo()">
            <span class="question-mark">?</span>
          </button>
        </h2>

      </div>
      <div class="customize-body">
        <!-- Theme Dropdown -->
        <div style="margin-bottom: 16px">
          <div class="theme-dropdown">
          <label for="theme-select" class="dropdown-label">Select a Theme</label>
          <select
            id="theme-select"
            v-model="selectedThemeName"
            @change="applyPreset"
            class="dropdown-select"
          >
            <option v-for="preset in themePresets" :key="preset.name" :value="preset.name">
              {{ preset.name }}
            </option>
          </select>
        </div>

        </div>

        <!-- Color Pickers -->
        <div class="color-grid">
          <div
            class="color-input"
            v-for="(entry, key) in visibleTheme"
            :key="key"
          >
            <label :for="key">{{ formatLabel(key) }}</label>
            <input
              type="color"
              v-model="theme[key].value"
              :id="key"
              class="color-picker"
            />
          </div>
        </div>
        <button type="button" @click="saveVenue()" class="btn btnAccent mt-2">Submit</button>
      </div>
    </div>

    <!-- Preview Section -->
    <component
      :style="{ backgroundColor: theme.screenBackgroundColor.value }"
      :is="getComponent(activeScreen)"
      :theme="theme"
      :venue=" venueHold"
    />
  </div>
</template>

<script>
import BarSelectionPreview from './previews/BarSelectionPreview.vue';
import MenuPreview from './previews/MenuPreview.vue';
import MessagePreview from './previews/MessagePreview.vue';
import swal from 'sweetalert';
import venueService from "../../api/venue.js"

const defaultTheme = {
  screenBackgroundColor: { value: '#5c5c5c', screens: ['Bar Selection', 'Menu', 'Messages'] },
  headerBackgroundColor: { value: '#242424', screens: ['Bar Selection', 'Menu','Messages'] },
  headerTextColor: { value: '#ffffff', screens: ['Bar Selection', 'Menu', 'Messages'] },
  tileBackgroundColor: { value: '#1e1e1e', screens: ['Bar Selection'] },
  PrimaryTextColor: { value: '#ffffff', screens: ['Bar Selection', 'Menu', 'Messages'] },
  SecondaryTextColor: { value: '#cccccc', screens: ['Bar Selection', 'Menu', 'Messages'] },
  accentBackgroundColor: { value: '#1E293B', screens: ['Menu', 'Menu Item'] },
  accentTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  altTextHeaderColor: { value: '#CBD5E1', screens: ['Menu'] },
  submitButtonColor: { value: '#000000', screens: ['Menu', 'Menu Item'] },
  infoIconColor: { value: '#000000', screens: ['Messages'] },
  borderColor: { value: '#000000', screens: ['Menu', 'Menu Item'] },
  submitButtonTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  displayInfoBackground: { value: '#4d4d4d', screens: ['Messages'] }
};

const white = {
  screenBackgroundColor: { value: '#ffffff', screens: ['Bar Selection', 'Menu', 'Messages'] },
  headerBackgroundColor: { value: '#f0f0f0', screens: ['Bar Selection', 'Menu','Messages'] },
  headerTextColor: { value: '#1a1a1a', screens: ['Bar Selection', 'Menu', 'Messages'] },
  tileBackgroundColor: { value: '#f9f9f9', screens: ['Bar Selection'] },
  PrimaryTextColor: { value: '#1a1a1a', screens: ['Bar Selection', 'Menu', 'Messages'] },
  SecondaryTextColor: { value: '#555555', screens: ['Bar Selection', 'Menu', 'Messages'] },
  accentBackgroundColor: { value: '#e0e0e0', screens: ['Menu', 'Menu Item'] },
  accentTextColor: { value: '#000000', screens: ['Menu', 'Menu Item'] },
  altTextHeaderColor: { value: '#333333', screens: ['Menu'] },
  submitButtonColor: { value: '#000000', screens: ['Menu', 'Menu Item'] },
  infoIconColor: { value: '#000000', screens: ['Messages'] },
  borderColor: { value: '#cccccc', screens: ['Menu', 'Menu Item'] },
  submitButtonTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  displayInfoBackground: { value: '#f5f5f5', screens: ['Messages'] }
};

const darkTheme = {
  screenBackgroundColor: { value: '#121212', screens: ['Bar Selection', 'Menu', 'Messages'] },
  headerBackgroundColor: { value: '#000000', screens: ['Bar Selection', 'Menu','Messages'] },
  headerTextColor: { value: '#ffffff', screens: ['Bar Selection', 'Menu', 'Messages'] },
  tileBackgroundColor: { value: '#1a1a1a', screens: ['Bar Selection'] },
  PrimaryTextColor: { value: '#f5f5f5', screens: ['Bar Selection', 'Menu', 'Messages'] },
  SecondaryTextColor: { value: '#aaaaaa', screens: ['Bar Selection', 'Menu', 'Messages'] },
  accentBackgroundColor: { value: '#333333', screens: ['Menu', 'Menu Item'] },
  accentTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  altTextHeaderColor: { value: '#dddddd', screens: ['Menu'] },
  submitButtonColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  infoIconColor: { value: '#525151', screens: ['Messages'] },
  borderColor: { value: '#3a3a3a', screens: ['Menu', 'Menu Item'] },
  submitButtonTextColor: { value: '#000000', screens: ['Menu', 'Menu Item'] },
  displayInfoBackground: { value: '#2a2a2a', screens: ['Messages'] }
};

const brightTheme = {
  screenBackgroundColor: { value: '#fef3c7', screens: ['Bar Selection', 'Menu', 'Messages'] },
  headerBackgroundColor: { value: '#facc15', screens: ['Bar Selection', 'Menu','Messages'] },
  headerTextColor: { value: '#1f2937', screens: ['Bar Selection', 'Menu', 'Messages'] },
  tileBackgroundColor: { value: '#fde68a', screens: ['Bar Selection'] },
  PrimaryTextColor: { value: '#1f2937', screens: ['Bar Selection', 'Menu', 'Messages'] },
  SecondaryTextColor: { value: '#4b5563', screens: ['Bar Selection', 'Menu', 'Messages'] },
  accentBackgroundColor: { value: '#fcd34d', screens: ['Menu', 'Menu Item'] },
  accentTextColor: { value: '#000000', screens: ['Menu', 'Menu Item'] },
  altTextHeaderColor: { value: '#92400e', screens: ['Menu'] },
  submitButtonColor: { value: '#1e40af', screens: ['Menu', 'Menu Item'] },
  infoIconColor: { value: '#1f2937', screens: ['Messages'] },
  borderColor: { value: '#fbbf24', screens: ['Menu', 'Menu Item'] },
  submitButtonTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  displayInfoBackground: { value: '#fcd34d', screens: ['Messages'] }
};


const strongTheme = {
  screenBackgroundColor: { value: '#1e1b4b', screens: ['Bar Selection', 'Menu', 'Messages'] },
  headerBackgroundColor: { value: '#4f46e5', screens: ['Bar Selection', 'Menu','Messages'] },
  headerTextColor: { value: '#ffffff', screens: ['Bar Selection', 'Menu', 'Messages'] },
  tileBackgroundColor: { value: '#3730a3', screens: ['Bar Selection'] },
  PrimaryTextColor: { value: '#ffffff', screens: ['Bar Selection', 'Menu', 'Messages'] },
  SecondaryTextColor: { value: '#d1d5db', screens: ['Bar Selection', 'Menu', 'Messages'] },
  accentBackgroundColor: { value: '#7c3aed', screens: ['Menu', 'Menu Item'] },
  accentTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  altTextHeaderColor: { value: '#c084fc', screens: ['Menu'] },
  submitButtonColor: { value: '#ec4899', screens: ['Menu', 'Menu Item'] },
  infoIconColor: { value: '#ffffff', screens: ['Messages'] },
  borderColor: { value: '#a855f7', screens: ['Menu', 'Menu Item'] },
  submitButtonTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  displayInfoBackground: { value: '#5b21b6', screens: ['Messages'] }
};

const accessibleTheme = {
  screenBackgroundColor: { value: '#f7f7f7', screens: ['Bar Selection', 'Menu', 'Messages'] }, // soft light gray
  headerBackgroundColor: { value: '#252525', screens: ['Bar Selection', 'Menu','Messages'] }, // dark neutral
  headerTextColor: { value: '#ffffff', screens: ['Bar Selection', 'Menu', 'Messages'] },
  tileBackgroundColor: { value: '#e0e0e0', screens: ['Bar Selection'] },
  PrimaryTextColor: { value: '#000000', screens: ['Bar Selection', 'Menu', 'Messages'] },
  SecondaryTextColor: { value: '#444444', screens: ['Bar Selection', 'Menu', 'Messages'] },
  accentBackgroundColor: { value: '#3182bd', screens: ['Menu', 'Menu Item'] }, // colorblind-safe blue
  accentTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  altTextHeaderColor: { value: '#08519c', screens: ['Menu'] }, // deeper blue
  submitButtonColor: { value: '#ef6548', screens: ['Menu', 'Menu Item'] }, // colorblind-safe orange
  infoIconColor: { value: '#252525', screens: ['Messages'] },
  borderColor: { value: '#969696', screens: ['Menu', 'Menu Item'] },
  submitButtonTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  displayInfoBackground: { value: '#d9d9d9', screens: ['Messages'] }
};

const modern = {
  screenBackgroundColor: { value: '#f5f5f5', screens: ['Bar Selection', 'Menu', 'Messages'] },
  headerBackgroundColor: { value: '#ffffff', screens: ['Bar Selection', 'Menu','Messages'] },
  headerTextColor: { value: '#111111', screens: ['Bar Selection', 'Menu', 'Messages'] },
  tileBackgroundColor: { value: '#ffffff', screens: ['Bar Selection'] },
  PrimaryTextColor: { value: '#1f1f1f', screens: ['Bar Selection', 'Menu', 'Messages'] },
  SecondaryTextColor: { value: '#6b7280', screens: ['Bar Selection', 'Menu', 'Messages'] },
  accentBackgroundColor: { value: '#6366f1', screens: ['Menu', 'Menu Item'] }, // soft indigo
  accentTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  altTextHeaderColor: { value: '#4b5563', screens: ['Menu'] },
  submitButtonColor: { value: '#111827', screens: ['Menu', 'Menu Item'] },
  infoIconColor: { value: '#000000', screens: ['Messages'] },
  borderColor: { value: '#d1d5db', screens: ['Menu', 'Menu Item'] },
  submitButtonTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  displayInfoBackground: { value: '#f3f4f6', screens: ['Messages'] }
};

const contemporary = {
  screenBackgroundColor: { value: '#f0f2f5', screens: ['Bar Selection', 'Menu', 'Messages'] },
  headerBackgroundColor: { value: '#1f2937', screens: ['Bar Selection', 'Menu','Messages'] },
  headerTextColor: { value: '#ffffff', screens: ['Bar Selection', 'Menu', 'Messages'] },
  tileBackgroundColor: { value: '#ffffff', screens: ['Bar Selection'] },
  PrimaryTextColor: { value: '#111827', screens: ['Bar Selection', 'Menu', 'Messages'] },
  SecondaryTextColor: { value: '#6b7280', screens: ['Bar Selection', 'Menu', 'Messages'] },
  accentBackgroundColor: { value: '#ec4899', screens: ['Menu', 'Menu Item'] }, // rose-pink accent
  accentTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  altTextHeaderColor: { value: '#f43f5e', screens: ['Menu'] },
  submitButtonColor: { value: '#0f172a', screens: ['Menu', 'Menu Item'] },
  infoIconColor: { value: '#ffffff', screens: ['Messages'] },
  borderColor: { value: '#e5e7eb', screens: ['Menu', 'Menu Item'] },
  submitButtonTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  displayInfoBackground: { value: '#e0e7ff', screens: ['Messages'] }
};

const sageTheme  = {
  screenBackgroundColor: { value: '#f5f7f4', screens: ['Bar Selection', 'Menu', 'Messages'] }, // soft off-white
  headerBackgroundColor: { value: '#A8B5A2', screens: ['Bar Selection', 'Menu', 'Messages'] }, // sage green
  headerTextColor: { value: '#1f2d20', screens: ['Bar Selection', 'Menu', 'Messages'] },
  tileBackgroundColor: { value: '#e6ede3', screens: ['Bar Selection'] }, // pale sage
  PrimaryTextColor: { value: '#2e3b2e', screens: ['Bar Selection', 'Menu', 'Messages'] }, // dark sage-gray
  SecondaryTextColor: { value: '#6c7a6e', screens: ['Bar Selection', 'Menu', 'Messages'] }, // muted herb
  accentBackgroundColor: { value: '#CBD5C0', screens: ['Menu', 'Menu Item'] }, // clean sage
  accentTextColor: { value: '#1a1a1a', screens: ['Menu', 'Menu Item'] },
  altTextHeaderColor: { value: '#556b5d', screens: ['Menu'] },
  submitButtonColor: { value: '#83977f', screens: ['Menu', 'Menu Item'] }, // deeper sage
  infoIconColor: { value: '#556b5d', screens: ['Messages'] },
  borderColor: { value: '#9ba89b', screens: ['Menu', 'Menu Item'] },
  submitButtonTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  displayInfoBackground: { value: '#e2e8dc', screens: ['Messages'] }
};


const goldTheme = {
  screenBackgroundColor: { value: '#1a1a1a', screens: ['Bar Selection', 'Menu', 'Messages'] },
  headerBackgroundColor: { value: '#d4af37', screens: ['Bar Selection', 'Menu', 'Messages'] },
  headerTextColor: { value: '#1a1a1a', screens: ['Bar Selection', 'Menu', 'Messages'] },
  tileBackgroundColor: { value: '#2b2b2b', screens: ['Bar Selection'] },
  PrimaryTextColor: { value: '#ffffff', screens: ['Bar Selection', 'Menu', 'Messages'] },
  SecondaryTextColor: { value: '#d6d3cb', screens: ['Bar Selection', 'Menu', 'Messages'] },
  accentBackgroundColor: { value: '#ffcc70', screens: ['Menu', 'Menu Item'] },
  accentTextColor: { value: '#1a1a1a', screens: ['Menu', 'Menu Item'] },
  altTextHeaderColor: { value: '#f1c40f', screens: ['Menu'] },
  submitButtonColor: { value: '#d4af37', screens: ['Menu', 'Menu Item'] },
  infoIconColor: { value: '#5c4426', screens: ['Messages'] }, // updated here
  borderColor: { value: '#a37e2c', screens: ['Menu', 'Menu Item'] },
  submitButtonTextColor: { value: '#1a1a1a', screens: ['Menu', 'Menu Item'] },
  displayInfoBackground: { value: '#2d2d2d', screens: ['Messages'] }
};

const terracottaTheme = {
  screenBackgroundColor: { value: '#fdf6f3', screens: ['Bar Selection', 'Menu', 'Messages'] }, // soft neutral rose
  headerBackgroundColor: { value: '#a44a3f', screens: ['Bar Selection', 'Menu', 'Messages'] }, // deep terracotta
  headerTextColor: { value: '#ffffff', screens: ['Bar Selection', 'Menu', 'Messages'] },
  tileBackgroundColor: { value: '#ffeae5', screens: ['Bar Selection'] },
  PrimaryTextColor: { value: '#3d1f1a', screens: ['Bar Selection', 'Menu', 'Messages'] },
  SecondaryTextColor: { value: '#6b4e49', screens: ['Bar Selection', 'Menu', 'Messages'] },
  accentBackgroundColor: { value: '#c86456', screens: ['Menu', 'Menu Item'] },
  accentTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  altTextHeaderColor: { value: '#bf5040', screens: ['Menu'] },
  submitButtonColor: { value: '#8e392c', screens: ['Menu', 'Menu Item'] },
  infoIconColor: { value: '#8e392c', screens: ['Messages'] },
  borderColor: { value: '#d6a79c', screens: ['Menu', 'Menu Item'] },
  submitButtonTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  displayInfoBackground: { value: '#f8ded6', screens: ['Messages'] }
};

const slateBlueTheme = {
  screenBackgroundColor: { value: '#f1f5f9', screens: ['Bar Selection', 'Menu', 'Messages'] },
  headerBackgroundColor: { value: '#334155', screens: ['Bar Selection', 'Menu', 'Messages'] }, // slate blue
  headerTextColor: { value: '#ffffff', screens: ['Bar Selection', 'Menu', 'Messages'] },
  tileBackgroundColor: { value: '#e2e8f0', screens: ['Bar Selection'] },
  PrimaryTextColor: { value: '#1e293b', screens: ['Bar Selection', 'Menu', 'Messages'] },
  SecondaryTextColor: { value: '#475569', screens: ['Bar Selection', 'Menu', 'Messages'] },
  accentBackgroundColor: { value: '#64748b', screens: ['Menu', 'Menu Item'] },
  accentTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  altTextHeaderColor: { value: '#475569', screens: ['Menu'] },
  submitButtonColor: { value: '#1e293b', screens: ['Menu', 'Menu Item'] },
  infoIconColor: { value: '#475569', screens: ['Messages'] },
  borderColor: { value: '#cbd5e1', screens: ['Menu', 'Menu Item'] },
  submitButtonTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  displayInfoBackground: { value: '#e2e8f0', screens: ['Messages'] }
};

const mutedPurpleTheme = {
  screenBackgroundColor: { value: '#f8f7fc', screens: ['Bar Selection', 'Menu', 'Messages'] },
  headerBackgroundColor: { value: '#5c5470', screens: ['Bar Selection', 'Menu', 'Messages'] },
  headerTextColor: { value: '#ffffff', screens: ['Bar Selection', 'Menu', 'Messages'] },
  tileBackgroundColor: { value: '#e8e6f1', screens: ['Bar Selection'] },
  PrimaryTextColor: { value: '#2c2a37', screens: ['Bar Selection', 'Menu', 'Messages'] },
  SecondaryTextColor: { value: '#6d6a7a', screens: ['Bar Selection', 'Menu', 'Messages'] },
  accentBackgroundColor: { value: '#8377b7', screens: ['Menu', 'Menu Item'] },
  accentTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  altTextHeaderColor: { value: '#7e6fb2', screens: ['Menu'] },
  submitButtonColor: { value: '#4c4177', screens: ['Menu', 'Menu Item'] },
  infoIconColor: { value: '#4c4177', screens: ['Messages'] },
  borderColor: { value: '#c7c4dd', screens: ['Menu', 'Menu Item'] },
  submitButtonTextColor: { value: '#ffffff', screens: ['Menu', 'Menu Item'] },
  displayInfoBackground: { value: '#e1def1', screens: ['Messages'] }
};

const themePresets = [
  { name: 'Default', value: defaultTheme },
  { name: 'White', value: white },
  { name: 'Dark', value: darkTheme },
  { name: 'Bright', value: brightTheme },
  { name: 'Strong', value: strongTheme },
  { name: 'Accessible', value: accessibleTheme },
  { name: 'Modern', value: modern },
  { name: 'Contemporary', value: contemporary },
  { name: 'Sage Green', value: sageTheme },
  { name: 'Gold', value: goldTheme },
  { name: 'Terracotta', value: terracottaTheme },
  { name: 'Slate Blue', value: slateBlueTheme },
  { name: 'Muted Purple', value: mutedPurpleTheme }
];

export default {
  name: 'ThemeEditor',
  props: ['venue','index'],
  components: {
    BarSelectionPreview,
    MenuPreview,
    MessagePreview
  },
  data() {
     const deepCopy = (obj) => JSON.parse(JSON.stringify(obj));
    return {
      activeScreen: 'Bar Selection',
      screens: ['Bar Selection', 'Menu',  'Messages'],
      selectedThemeName: 'Default',
      theme: deepCopy(defaultTheme),
      defaultTheme,
      themePresets,
      venueHold:''
    };
  },
  mounted(){
    if(this.$props.venue.theme){
      this.theme = JSON.parse(JSON.stringify(this.$props.venue.theme)); 
    }
    this.venueHold = JSON.parse(JSON.stringify(this.$props.venue));
  },
  methods: {
        async saveVenue(){
          this.venueHold.theme = this.theme

        if(this.currentClubID!=0){
            venueService.updateVenueByID( this.venueHold._id, this.venueHold)
      .then(data =>{
        if(data.error){
          alert(data.message)
        }
        else{
          swal("Update Successful")
          console.log("Modal pop-up Update successful!")
          const dataObject = {index:this.$props.index,venue:data}
        this.$store.dispatch('updateVenue',dataObject)
        this.$router.push({ path: '/Venue-List/' })
        }
        

      });
        }  
    },
    imageInfo(){
    swal(
    "Theme Editor",
    "This preview shows what users will see. Customize your app's color scheme here.\n\nNote: 'Screen Background', 'Header Background', 'Header Text', 'Primary Text', and 'Secondary Text' colors are shared across all screens."
    );
    },
    formatLabel(key) {
      return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
    },
     applyPreset() {
      const preset = this.themePresets.find(p => p.name === this.selectedThemeName);
      if (preset) {
        for (const key in preset.value) {
          if (this.theme[key]) {
            this.theme[key].value = preset.value[key].value;
          }
        }
      }
    },

    getComponent(screen) {
      switch (screen) {
        case 'Bar Selection':
          return 'BarSelectionPreview';
        case 'Menu':
          return 'MenuPreview';
        case 'Messages':
          return 'MessagePreview';
      }
    }
  },
  computed: {
    displayTheme(){
      return this.theme
    },
  visibleTheme() {
    const result = {};
    for (const key in this.theme) {
      const entry = this.theme[key];
      if (!entry.screens || entry.screens.includes(this.activeScreen)) {
        result[key] = entry;
      }
    }
    return result;
  }
}

};
</script>

<style scoped>
.theme-editor {
  padding: 16px;
  min-height: 100vh;
}

.tab-row {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.tab-btn {
  padding: 8px 16px;
  border: none;
  background: #333;
  color: white;
  border-radius: 6px;
  cursor: pointer;
}

.tab-btn.active {
  background: purple;
}

.customize-card {
  background: #f0f0f0;
  border-radius: 12px;
  margin-bottom: 24px;
  overflow: hidden;
}

.customize-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #2d2d2d;
  color: white;
  padding: 12px 16px;
}

.customize-body {
  background: #e0e0e0;
  padding: 16px;
}

.color-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.color-input {
  flex: 1 1 calc(25% - 16px);
  min-width: 140px;
}

.color-picker {
  width: 100%;
  height: 40px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.reset-btn {
  background: #ff4d4d;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
}
.theme-dropdown {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
}

.dropdown-label {
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 14px;
  color: #2d2d2d;
}

.dropdown-select {
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #cccccc;
  background-color: #ffffff;
  font-size: 14px;
  color: #2d2d2d;
  outline: none;
  transition: border 0.2s ease;
}

.dropdown-select:focus {
  border-color: #4F47C7;
  box-shadow: 0 0 0 2px rgba(79, 71, 199, 0.2);
}

.dropdown-select option {
  padding: 8px;
}

</style>
